<style lang="scss" scoped>
@use 'sass:color';
@use '../../palette.scss' as *;

$input-box-width: 250px;

.flex {
    display: flex;
    align-items: baseline;

    .prop {
        width: 66%;

        &.name {
            font-weight: bold;
            width: 33%;
        }
    }

    .input-box {
        margin-bottom: 10px;
        display: flex;
        justify-content: center;
        width: 100%;

        &.dropdown {
            min-width: $input-box-width;
            margin-top: 10px;

            ::v-deep(.dropdown-container) {
                width: 100%;
            }
        }
    }
}

.user-list {
    display: flex;
    flex-flow: column;
    position: relative;

    .button-block {
        display: flex;
        justify-content: flex-end;
        align-items: center;
        margin-bottom: 10px;
        margin-right: 11px;
    }

    .user-card-container {
        display: flex;
        align-items: center;
        position: relative;

        &:hover,
        &.swiped {
            .user-card {
                margin-right: 55px;
            }
        }

        .delete-btn-ctn {
            position: absolute;
            width: 55px;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            top: 0;
            right: 0;
            z-index: 0;

            button {
                margin: 0 5px 10px 0;
            }

            button {
                background-color: transparent;
                color: $theme-red;
                font-size: 1.7em;
                border: none;
                outline: none;

                i {
                    border-radius: 50%;

                    &:hover {
                        background-color: color.adjust(
                            $theme-red,
                            $alpha: -0.85
                        );
                        color: color.adjust($theme-red, $lightness: -5%);
                    }

                    &:active {
                        transform: scale(0.97);
                    }
                }
            }
        }

        .user-card {
            display: flex;
            flex-direction: column;
            color: $theme-dark-grey;
            border-radius: 5px;
            background-color: white;
            padding: 15px;
            box-shadow: $subtle-shadow;
            border: 1px solid $theme-light-grey;
            margin-bottom: 10px;
            width: 100%;
            transition: margin 0.25s;
            z-index: 5;

            &:active {
                transform: scale(0.99);
            }

            .admin-icon {
                position: absolute;
                color: $theme-blue;
            }

            &:hover {
                cursor: pointer;
                background-color: color.adjust(white, $lightness: -5%);
            }

            .content {
                margin-left: 30px;
            }

            &.spaced {
                margin-bottom: 10px;
            }

            .name {
                font-weight: bold;
                font-size: 1.3em;
            }

            .prop-block {
                display: flex;
                flex-flow: column;

                .property {
                    display: flex;
                    align-items: baseline;
                    padding: 10px;
                    min-width: 33%;

                    p {
                        margin: 0;
                        word-break: break-word;
                    }

                    .property-name {
                        font-weight: bold;
                        margin-right: 8px;
                    }
                }
            }
        }
    }
}

.header-content {
    display: flex;
    align-items: baseline;

    button {
        margin-left: 10px;
        padding: 0 5px 0 5px;
    }
}

@media (min-width: 992px) {
    .user-list .user-card .prop-block {
        flex-flow: row nowrap;
        justify-content: space-between;
    }
}

::v-deep(.modal-footer button) {
    margin-right: 10px;
}

::v-deep(.modal-footer button:last-child) {
    margin-right: 0;
}
</style>

<template>
    <Card fullWidth color="blue" :maxWidth="'800px'">
        <template #header>{{ $t('user_plural') }}</template>
        <template #body>
            <div class="user-list">
                <div class="button-block">
                    <Button @click="openNewUserDialog" outline circle
                        ><i class="fa fa-plus"
                    /></Button>
                </div>
                <v-touch
                    v-for="(user, index) in userList"
                    :key="user.id"
                    v-touch:swipe.left="() => swipeUserLeft(user, index)"
                    v-touch:swipe.right="() => swipeUserRight(index)"
                    class="user-card-container"
                    :class="{
                        spaced: index !== users.length - 1,
                        swiped: swipeIndex === index,
                    }"
                >
                    <div class="user-card" @click="selectUser(user)">
                        <div
                            class="admin-icon"
                            v-if="user.role === 'server_admin'"
                            v-tooltip.bottom="$t('serverAdmin')"
                        >
                            <i class="fa fa-user-cog"></i>
                        </div>
                        <div
                            class="admin-icon"
                            v-if="user.role === 'campaign_manager'"
                            v-tooltip.bottom="$t('campaignManager')"
                        >
                            <i class="fa fa-user-plus"></i>
                        </div>
                        <div class="content">
                            <div class="title">{{ user.name }}</div>
                            <div class="description">
                                {{ user.description }}
                            </div>
                        </div>
                    </div>
                    <div class="delete-btn-ctn" v-if="canDeleteUser(user)">
                        <button @click="promptUserDelete(user)">
                            <i class="far fa-times-circle" />
                        </button>
                    </div>
                </v-touch>
            </div>
        </template>
    </Card>

    <!-- Delete verification modal -->
    <Modal
        v-if="showDeleteVerify"
        @ok="userDelete()"
        @cancel="showDeleteVerify = false"
    >
        <template #header>
            <h3>{{ $t('verifyDelete') }}</h3>
        </template>
        <template #body>
            <div class="form-group">
                {{ $t('verifyUserDelete', { user_name: userToDelete.name }) }}
            </div>
        </template>
    </Modal>

    <!-- Add New User modal -->
    <Modal
        v-if="showNewUser"
        @ok="addNewUser"
        @cancel="showNewUser = false"
        :confirmDisabled="!canConfirmNewUser"
    >
        <template #header>
            <h3>{{ $t('addNewUser') }}</h3>
        </template>
        <template #body>
            <div>
                <!-- Name -->
                <div class="flex">
                    <div class="prop name">{{ $t('name') }}</div>
                    <div class="prop">
                        <div class="input-box">
                            <InputBox
                                type="text"
                                center
                                v-model="newUser.name"
                                ref="nameInput"
                            />
                        </div>
                    </div>
                </div>

                <!-- Email -->
                <div class="flex">
                    <div class="prop name">{{ $t('email') }}</div>
                    <div class="prop">
                        <div class="input-box">
                            <InputBox
                                type="text"
                                center
                                v-model="newUser.email"
                                placeholder="user@example.com"
                            />
                        </div>
                    </div>
                </div>

                <!-- Role -->
                <div class="flex">
                    <div class="prop name">{{ $t('role') }}</div>
                    <div class="prop">
                        <div class="input-box dropdown">
                            <DropdownMenu
                                :options="displayedRoles"
                                outline
                                @change="setRoleCommand"
                                :value="newUser.role"
                            />
                        </div>
                    </div>
                </div>

                <!-- Description -->
                <div class="flex">
                    <div class="prop name">{{ $t('description') }}</div>
                    <div class="prop">
                        <div class="input-box">
                            <InputBox
                                type="text"
                                center
                                v-model="newUser.description"
                                :placeholder="`**${$t('optional')}**`"
                            />
                        </div>
                    </div>
                </div>

                <!-- Organization -->
                <div v-if="!this.disableOrganization" class="flex">
                    <div class="prop name">{{ $t('organization') }}</div>
                    <div class="prop">
                        <div class="input-box dropdown">
                            <DropdownMenu
                                :options="sortedOrganizations"
                                outline
                                max-height="200px"
                                @change="setOrganizationCommand"
                                :value="newUser.organization_id"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </Modal>
</template>

<script>
import i18next from 'i18next';
import toastr from 'toastr';
import { mapGetters } from 'vuex';

import { addUser, getUsersList, deleteUser } from '../../api/user';
import { changeMonitor } from '../../api/ChangeMonitor';
import * as client from '../../api/client';

export default {
    data() {
        return {
            newUser: undefined,
            roles: [
                { label: i18next.t('serverAdmin'), value: 'server_admin' },
                {
                    label: i18next.t('campaignManager'),
                    value: 'campaign_manager',
                },
                {
                    label: i18next.t('organizationAdmin'),
                    value: 'organization_admin',
                },
                { label: i18next.t('normalUser'), value: 'standard' },
            ],
            showDeleteVerify: false,
            showNewUser: false,
            userToDelete: undefined,
            users: [],
            swipeIndex: -1, // The index of the user that has been swiped on mobile
            showDeleteVerify: false, // Delete confirm modal
            disableOrganization: true,
        };
    },

    computed: {
        ...mapGetters(['isUserAdmin']),

        currentUser() {
            return this.$store.state.currentUser;
        },

        userList() {
            return this.users.sort(this.$store.getters.intlCompareByName);
        },

        // sorts the organizations alphabetically by label for the organizations dropdown menu display
        sortedOrganizations() {
            return this.organizations.slice().sort((a, b) => {
                return a.label.localeCompare(b.label);
            });
        },

        organizations() {
            return this.$store.state.organizations.map((org) => ({
                label: org.organization_name,
                value: Number(org.id),
            }));
        },

        displayedRoles() {
            if (this.isUserAdmin) {
                return this.roles;
            }
            return this.roles.slice(1);
        },

        canConfirmNewUser() {
            if (!this.newUser) return false;

            const hasName = this.newUser.name?.trim();
            const hasEmail = this.isValidEmail(this.newUser.email);
            const hasRole = this.newUser.role;

            // Organization is required only if it is enabled
            const hasOrganization =
                this.disableOrganization || this.newUser.organization_id;

            return Boolean(hasName && hasEmail && hasRole && hasOrganization);
        },
    },

    created: async function () {
        await this.syncUsers();
        this.initializeNewUser();

        changeMonitor.addNotifiedUser(this.handleChanges);
    },

    beforeDestroy() {
        changeMonitor.removeNotifiedUser(this.handleChanges);
    },

    methods: {
        canDeleteUser(user) {
            if (!user || !this.currentUser) {
                return false;
            }

            return Number(user.id) !== Number(this.currentUser.id);
        },

        async addNewUser() {
            if (!this.canConfirmNewUser) return;
            this.showNewUser = false;
            const response = await addUser(this.newUser);
            if (response.status === client.STATUS_OK) {
                toastr.success(this.$t('userCreateSuccessful'));
            } else if (response.status === client.STATUS_ERROR) {
                if (response.msg) {
                    toastr.error(this.$t(response.msg, response.msg_data));
                } else {
                    toastr.error(this.$t('userCreateFailed'));
                }
            }
            this.initializeNewUser();
        },

        initializeNewUser() {
            this.newUser = {
                email: '',
                name: '',
                role: this.displayedRoles[0].value,
                description: '',
                organization_id: 0,
            };
        },

        handleChanges(change) {
            if (change.changeType === 'USER') {
                for (let i = 0; i < this.users.length; i++) {
                    // We've found the user in question
                    if (this.users[i].id === change.data.id) {
                        // Always delete and readd the object so vue triggers a rerender
                        this.users.splice(i, 1);
                        // If the user was deleted, leave it that way
                        if (!change.data.deleted) {
                            this.users.push({ ...change.data });
                        }
                        return;
                    }
                }
                // We didn't find our user so it must be new
                this.users.push({ ...change.data });
            }
        },

        openNewUserDialog() {
            // Always start with a fresh form so a previous role/org selection doesn't carry over
            this.initializeNewUser();
            this.disableOrganization = this.newUser.role === 'server_admin';
            this.showNewUser = true;
            this.$nextTick(() => this.$refs.nameInput.setFocus());
        },

        async syncUsers() {
            const users = await getUsersList();
            this.users = users.payload;
        },

        selectUser(user) {
            this.$router.push({ path: `/admin/users/${user.id}` });
        },

        // Update role on user object
        setRoleCommand(value) {
            this.newUser.role = value;

            if (value === 'server_admin') {
                this.disableOrganization = true;
                this.newUser.organization_id = 0;
            } else {
                this.disableOrganization = false;
                this.newUser.organization_id = 0;
            }
        },

        // Update organization on user object
        setOrganizationCommand(value) {
            this.newUser.organization_id = value;
        },

        swipeUserLeft(user, index) {
            if (!this.canDeleteUser(user)) {
                return;
            }
            this.swipeIndex = index;
        },

        promptUserDelete(user) {
            if (!this.canDeleteUser(user)) {
                return;
            }
            this.userToDelete = user;
            this.showDeleteVerify = true;
        },

        async userDelete() {
            if (!this.canDeleteUser(this.userToDelete)) {
                this.showDeleteVerify = false;
                return;
            }

            this.showDeleteVerify = false;
            const response = await deleteUser(this.userToDelete.id);
            if (response.status === client.STATUS_OK) {
                toastr.success(this.$t('userDeleteSuccessful'));
            } else if (response.status === client.STATUS_ERROR) {
                toastr.error(this.$t('problemDeletingUser'));
            }

            // update the list of users after the delete action
            await this.syncUsers();
        },

        // Responsible for hiding the delete button for a particular user if it's
        //  swiped closed
        swipeUserRight(index) {
            if (index === this.swipeIndex) {
                this.swipeIndex = -1; // "Unswipe" the current user
            }
            // If the id doesn't match, swiping right doesn't do anything
        },

        isValidEmail(email) {
            return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
        },
    },
};
</script>
